// Mesh overlay — draws clean MediaPipe landmarks + connection lines on top
// of the running microgame. Color palette matches muyu_ios DemoHubView so the
// look is familiar.

import {
  FaceLandmarker,
  HandLandmarker,
  PoseLandmarker,
} from '@mediapipe/tasks-vision';

/** Connection { start, end } isn't part of the public types — re-declare locally. */
interface Connection {
  start: number;
  end: number;
}
import type {
  FaceFrame,
  HandsFrame,
  InputFrame,
  Microgame,
  PoseFrame,
  Point,
} from '@pose-game/core';

export type DrawRequires = Microgame['requires'];

interface MeshTheme {
  hand: { line: string; dot: string; lineWidth: number; dotR: number };
  pose: { line: string; dot: string; lineWidth: number; dotR: number };
  face: {
    tess: string;
    oval: string;
    lips: string;
    eyes: string;
    iris: string;
    brows: string;
    lineW: number;
  };
}

const THEME: MeshTheme = {
  hand: { line: '#629fff', dot: '#fce485', lineWidth: 4, dotR: 4 },
  pose: { line: '#34d49a', dot: '#fce485', lineWidth: 5, dotR: 5 },
  face: {
    tess: 'rgba(33, 213, 238, 0.32)',
    oval: 'rgba(230, 232, 235, 0.95)',
    lips: '#f573b3',
    eyes: '#34d49a',
    iris: '#629fff',
    brows: 'rgba(245, 245, 245, 0.7)',
    lineW: 2,
  },
};

export function drawMeshOverlay(
  g: CanvasRenderingContext2D,
  frame: InputFrame,
  requires: DrawRequires,
): void {
  g.save();
  if (requires.pose === true) drawPoseMesh(g, frame.pose);
  if (requires.hands === true) drawHandsMesh(g, frame.hands);
  if (requires.face === true) drawFaceMesh(g, frame.face);
  g.restore();
}

function drawHandsMesh(g: CanvasRenderingContext2D, hands: HandsFrame): void {
  if (hands.left.visible) drawHand(g, hands.left.landmarks);
  if (hands.right.visible) drawHand(g, hands.right.landmarks);
}

function drawHand(g: CanvasRenderingContext2D, pts: Point[]): void {
  if (pts.length === 0) return;
  drawConnections(g, pts, HandLandmarker.HAND_CONNECTIONS, THEME.hand.line, THEME.hand.lineWidth);
  drawDots(g, pts, THEME.hand.dot, THEME.hand.dotR);
}

function drawPoseMesh(g: CanvasRenderingContext2D, pose: PoseFrame): void {
  if (!pose.visible || pose.landmarks.length < 33) return;
  drawConnections(g, pose.landmarks, PoseLandmarker.POSE_CONNECTIONS, THEME.pose.line, THEME.pose.lineWidth);
  drawDots(g, pose.landmarks, THEME.pose.dot, THEME.pose.dotR);
}

function drawFaceMesh(g: CanvasRenderingContext2D, face: FaceFrame): void {
  if (!face.visible || face.landmarks.length < 468) return;
  const pts = face.landmarks;
  const t = THEME.face;
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_TESSELATION, t.tess, 1);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_FACE_OVAL, t.oval, 3);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_LIPS, t.lips, t.lineW + 0.5);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_LEFT_EYE, t.eyes, t.lineW);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE, t.eyes, t.lineW);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_LEFT_EYEBROW, t.brows, t.lineW);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYEBROW, t.brows, t.lineW);
  // Iris connections may have extra landmarks (>468); guard inside drawConnections.
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS, t.iris, t.lineW);
  drawConnections(g, pts, FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS, t.iris, t.lineW);
}

function drawConnections(
  g: CanvasRenderingContext2D,
  pts: Point[],
  conns: Connection[],
  color: string,
  lineWidth: number,
): void {
  g.strokeStyle = color;
  g.lineWidth = lineWidth;
  g.lineCap = 'round';
  g.beginPath();
  for (const c of conns) {
    const a = pts[c.start];
    const b = pts[c.end];
    if (a == null || b == null) continue;
    g.moveTo(a.x, a.y);
    g.lineTo(b.x, b.y);
  }
  g.stroke();
}

function drawDots(g: CanvasRenderingContext2D, pts: Point[], color: string, r: number): void {
  g.fillStyle = color;
  for (const p of pts) {
    g.beginPath();
    g.arc(p.x, p.y, r, 0, Math.PI * 2);
    g.fill();
  }
}
